import { FC, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useOktaAuth } from '@okta/okta-react'

import {
	Alert,
	Box,
	Button,
	Container,
	ContentLayout,
	Header,
	Modal,
	SpaceBetween,
	Textarea
} from '@cloudscape-design/components'

import AccountNetworkReadonlyView from '../../../../components/Account/AccountNetworkReadonlyView'
import StgNetworkApprovalSection from '../../../../components/Account/StgNetworkApprovalSection'
import StgRequestSummaryView from '../../../../components/Account/StgRequestSummaryView'
import LoadingSpinner from '../../../../components/LoadingSpinner'
import CONSTANTS from '../../../../constants'
import hasRequiredPermissions from '../../../../helper/hasRequiredPermissions'
import useSingleAccount from '../../../../hooks/query/useSingleAccount'
import useUserPermissions from '../../../../hooks/query/useUserPermissions'
import useStgRejectAccount from '../../../../hooks/mutate/useStgRejectAccount'
import { accountHasNetworkRequestData } from '../../../../helper/accountRequestNetworkSummary'
import { getAccountPath } from '../../../../helper/accountRoutes'

const AccountStgReview: FC = () => {
	const CURRENT_USER_ID = JSON.parse(localStorage['okta-token-storage'])[
		'idToken'
	]['claims']['email'] as string

	const {
		data: access,
		isLoading: isLoadingPermissions,
		isError: isPermissionsError
	} = useUserPermissions(CURRENT_USER_ID)

	const { id: accountRequestID } = useParams() as { id: string }
	const navigate = useNavigate()
	const { authState } = useOktaAuth()
	const thisUserEmail = authState?.idToken?.claims.email as string

	const { data: account, isLoading } = useSingleAccount(accountRequestID, true)

	const { mutate: rejectStg, isLoading: isRejecting } =
		useStgRejectAccount(accountRequestID)

	const [rejectModalVisible, setRejectModalVisible] = useState(false)
	const [rejectComment, setRejectComment] = useState('')
	const [actionError, setActionError] = useState<string | null>(null)

	const hasApproverPermissions = Boolean(
		access?.permissions &&
			hasRequiredPermissions(CONSTANTS.ACCOUNT_APPROVER, access.permissions)
	)

	if (!accountRequestID) {
		return (
			<Alert type="error" header="Missing account">
				No account id in the URL.
			</Alert>
		)
	}

	if (isLoading) return <LoadingSpinner />

	if (!account || account.status === 'ERROR') {
		return (
			<Alert type="error" header="Could not load account">
				{(account?.details as { message?: string } | undefined)?.message ??
					'Unknown error'}
			</Alert>
		)
	}

	const backToAccount = () =>
		navigate(getAccountPath(accountRequestID), { replace: true })

	const stg = account.stgRequest

	if (account.status !== 'APPROVED') {
		return (
			<ContentLayout
				header={<Header variant="h1">Staging Network Review</Header>}
			>
				<SpaceBetween direction="vertical" size="l">
					<Alert type="info" header="Not available">
						Staging network review applies only to approved accounts.
					</Alert>
					<Button onClick={backToAccount}>Back to account</Button>
				</SpaceBetween>
			</ContentLayout>
		)
	}

	if (!stg) {
		return (
			<ContentLayout
				header={<Header variant="h1">Staging Network Review</Header>}
			>
				<SpaceBetween direction="vertical" size="l">
					<Alert type="info" header="No STG request">
						This account does not have a staging network follow-up on record.
					</Alert>
					<Button onClick={backToAccount}>Back to account</Button>
				</SpaceBetween>
			</ContentLayout>
		)
	}

	const isStgRequestor =
		Boolean(stg.requestorID) &&
		CURRENT_USER_ID.trim().toLowerCase() ===
			String(stg.requestorID).trim().toLowerCase()

	const canActOnStg = hasApproverPermissions && !isStgRequestor

	const detailsForNetwork: Record<string, unknown> | undefined =
		stg.status === 'APPROVED'
			? (account.details as Record<string, unknown> | undefined)
			: (stg.details as Record<string, unknown> | undefined)

	const showNetworkRequestSummary = accountHasNetworkRequestData(detailsForNetwork)

	const onRejectConfirm = () => {
		setActionError(null)
		rejectStg(
			{
				action: 'stg_reject',
				accountRequestID: account.accountRequestID,
				commenterID: thisUserEmail,
				comment: rejectComment.trim()
			},
			{
				onSuccess: () => {
					setRejectModalVisible(false)
					setRejectComment('')
					backToAccount()
				},
				onError: (e: unknown) =>
					setActionError(e instanceof Error ? e.message : String(e))
			}
		)
	}

	const requestorDisplay =
		stg.requestorID?.trim() || account.requestorID?.trim() || null
	const approverDisplay = stg.approverID?.trim() || null

	const statusAlert =
		stg.status === 'PENDING_APPROVAL' ? (
			!canActOnStg ? (
				<Alert type="info" header="Pending approval">
					Your stage environment request is awaiting review. See the request details
					below.
				</Alert>
			) : null
		) : stg.status === 'APPROVED' ? (
			<Alert type="success" header="Staging network request approved">
				<SpaceBetween direction="vertical" size="s">
					<span>
						This staging network request was approved
						{stg.approvedAt ? ` (${stg.approvedAt}).` : '.'}
					</span>
					<SpaceBetween direction="vertical" size="xs">
						<div>
							<Box variant="awsui-key-label">Requestor</Box>
							{requestorDisplay ?? '—'}
						</div>
						<div>
							<Box variant="awsui-key-label">Approver</Box>
							{approverDisplay ?? '—'}
						</div>
					</SpaceBetween>
				</SpaceBetween>
			</Alert>
		) : (
			<Alert type="warning" header="STG request rejected">
				<SpaceBetween direction="vertical" size="s">
					{stg.rejectComment ? (
						<SpaceBetween direction="vertical" size="xs">
							<span>This request was rejected.</span>
							<span>
								<strong>Comment:</strong> {stg.rejectComment}
							</span>
						</SpaceBetween>
					) : (
						<span>This staging network request was rejected.</span>
					)}
					<SpaceBetween direction="vertical" size="xs">
						<div>
							<Box variant="awsui-key-label">Requestor</Box>
							{requestorDisplay ?? '—'}
						</div>
						<div>
							<Box variant="awsui-key-label">Rejected by</Box>
							{stg.rejectorID?.trim() ?? '—'}
						</div>
						{stg.rejectedAt ? (
							<div>
								<Box variant="awsui-key-label">Rejected at</Box>
								{stg.rejectedAt}
							</div>
						) : null}
					</SpaceBetween>
				</SpaceBetween>
			</Alert>
		)

	const pendingNeedsPermissions =
		stg.status === 'PENDING_APPROVAL' && (isLoadingPermissions || isPermissionsError)

	if (pendingNeedsPermissions) {
		if (isPermissionsError) {
			return (
				<ContentLayout header={<Header variant="h1">Staging Network Review</Header>}>
					<SpaceBetween direction="vertical" size="l">
						<Alert type="error" header="Permissions">
							Could not load your SSP permissions.
						</Alert>
						<Button onClick={backToAccount}>Back to account</Button>
					</SpaceBetween>
				</ContentLayout>
			)
		}
		return <LoadingSpinner />
	}

	const readOnlyNetworkSection =
		showNetworkRequestSummary && (!canActOnStg || stg.status !== 'PENDING_APPROVAL') ? (
			<AccountNetworkReadonlyView
				details={detailsForNetwork}
				accountType={account.type}
				networkSectionTitle={
					stg.status === 'APPROVED'
						? 'Approved staging network'
						: stg.status === 'REJECTED'
						  ? 'Submitted staging network'
						  : 'Network details'
				}
				networkSectionHeaderVariant="h2"
				ipTableHeaderVariant="h3"
			/>
		) : null

	const projectLabel =
		(account.details as { projectName?: string } | undefined)?.projectName ??
		account.accountRequestID

	const pageTitle = stg.status === 'APPROVED' ? 'Staging network' : 'Staging Network Review'

	const pageDescription =
		stg.status === 'APPROVED'
			? `${projectLabel} · Approved`
			: projectLabel

	const showRequestorPendingDetails =
		stg.status === 'PENDING_APPROVAL' && !canActOnStg

	const showApproverForm = stg.status === 'PENDING_APPROVAL' && canActOnStg

	return (
		<ContentLayout
			header={
				<Header variant="h1" description={pageDescription}>
					{pageTitle}
				</Header>
			}
		>
			<SpaceBetween direction="vertical" size="l">
				<Button variant="link" onClick={backToAccount}>
					← Back to account
				</Button>

				{actionError && (
					<Alert
						type="error"
						dismissible
						onDismiss={() => setActionError(null)}
						header="Action failed"
					>
						{actionError}
					</Alert>
				)}

				{showApproverForm ? (
					<StgNetworkApprovalSection
						account={account}
						thisUserEmail={thisUserEmail}
						onApproved={backToAccount}
						onCancel={backToAccount}
						onApiError={setActionError}
						onRejectClick={() => {
							setActionError(null)
							setRejectModalVisible(true)
						}}
					/>
				) : (
					<SpaceBetween direction="vertical" size="l">
						{showRequestorPendingDetails && (
							<>
								{statusAlert && <Container>{statusAlert}</Container>}
								<StgRequestSummaryView account={account} stg={stg} />
							</>
						)}
						{(stg.status === 'APPROVED' || stg.status === 'REJECTED') && (
							<Container>
								<SpaceBetween direction="vertical" size="xl">
									{statusAlert}
									{readOnlyNetworkSection}
									{!showNetworkRequestSummary && (
										<Alert type="info" header="No network summary">
											No structured network fields are stored on this record.
										</Alert>
									)}
								</SpaceBetween>
							</Container>
						)}
					</SpaceBetween>
				)}

				<Modal
					visible={rejectModalVisible}
					onDismiss={() => !isRejecting && setRejectModalVisible(false)}
					header="Reject STG network request"
					footer={
						<Box float="right">
							<SpaceBetween direction="horizontal" size="xs">
								<Button
									variant="link"
									disabled={isRejecting}
									onClick={() => setRejectModalVisible(false)}
								>
									Cancel
								</Button>
								<Button
									variant="primary"
									loading={isRejecting}
									disabled={!rejectComment.trim() || !canActOnStg}
									onClick={onRejectConfirm}
								>
									Reject STG request
								</Button>
							</SpaceBetween>
						</Box>
					}
				>
					<Textarea
						value={rejectComment}
						onChange={({ detail }) => setRejectComment(detail.value)}
						placeholder="Reason for rejection"
					/>
				</Modal>
			</SpaceBetween>
		</ContentLayout>
	)
}

export default AccountStgReview
